import moment, { Moment } from 'moment-timezone'
import ms from 'ms'

export const offsetToUsaTimezoneMap: { [key: number]: string } = {
  '-4': 'America/New_York', // Eastern Daylight Time (EDT)
  '-5': 'America/New_York', // Eastern Standard Time (EST)
  '-6': 'America/Chicago', // Central Standard Time (CST)
  '-7': 'America/Denver', // Mountain Standard Time (MST)
  '-8': 'America/Los_Angeles', // Pacific Standard Time (PST)
  '-9': 'America/Anchorage', // Alaska Standard Time (AKST)
  '-10': 'Pacific/Honolulu' // Hawaii-Aleutian Standard Time (HST)
}

export type CalendarDateInOffsetOpts = {
  includeTime?: boolean
  includeYear?: boolean
  includeOrdinal?: boolean
}

export function normalizeUtcOffsetHours(
  offset?: number | string | null
): number | undefined {
  if (offset === undefined || offset === null || offset === '') {
    return undefined
  }

  const hours = typeof offset === 'string' ? Number(offset) : offset
  return Number.isFinite(hours) ? hours : undefined
}

export function getUsaTimezoneFromOffset(
  offsetHours: number
): string | undefined {
  const map = offsetToUsaTimezoneMap as Record<string, string>
  return map[String(offsetHours)]
}

export function parseUtcDate(value?: string | Date): Moment | null {
  if (!value) {
    return null
  }

  const parsed = moment.utc(value)
  return parsed.isValid() ? parsed : null
}

export function toUtcIsoString(value?: string | Date): string | undefined {
  const parsed = parseUtcDate(value)
  return parsed?.toISOString()
}

export function toZonedMoment(
  value?: string | Date,
  offsetHours?: number
): Moment | null {
  const parsed = parseUtcDate(value)
  if (!parsed) {
    return null
  }

  if (offsetHours === undefined) {
    return parsed.local()
  }

  return parsed.utcOffset(offsetHours * 60)
}

export function nowInUtcOffset(offsetHours?: number): Moment {
  if (offsetHours === undefined) {
    return moment()
  }

  return moment.utc().utcOffset(offsetHours * 60)
}

export function isSameCalendarDayInOffset(
  a?: string | Date,
  b?: string | Date,
  offsetHours?: number
): boolean {
  const momentA = toZonedMoment(a, offsetHours)
  const momentB = toZonedMoment(b, offsetHours)

  if (!momentA || !momentB) {
    return false
  }

  return momentA.isSame(momentB, 'day')
}

export function formatCalendarDateInOffset(
  value?: string | Date,
  offsetHours?: number,
  opts?: CalendarDateInOffsetOpts
): string | undefined {
  const date = toZonedMoment(value, offsetHours)
  if (!date) {
    return undefined
  }

  const now = nowInUtcOffset(offsetHours)
  const isCurrentYear = now.year() === date.year()
  const displayYear = !isCurrentYear && opts?.includeYear ? ', YYYY' : ''
  const displayOrdinal = opts?.includeOrdinal ? 'Do' : 'D'
  const timeFormat = opts?.includeTime ? ' h:mm A' : ''

  return date.calendar(now, {
    nextWeek: '[Next] dddd' + timeFormat,
    nextDay: '[Tomorrow]' + timeFormat,
    sameDay: '[Today]' + timeFormat,
    lastDay: '[Yesterday]' + timeFormat,
    lastWeek: 'dddd' + timeFormat,
    sameElse: `MMM ${displayOrdinal}${displayYear}` + timeFormat
  })
}

export function formatMessageTimeInOffset(
  value?: string | Date,
  offsetHours?: number
): string | undefined {
  const date = toZonedMoment(value, offsetHours)
  return date?.format('h:mm A')
}

const minute = ms('1m')
const hour = ms('1hr')
const day = ms('1d')
const month = day * (364.25 / 12)
const year = day * 364.25

export function formatRelativeTimeCondensed(
  value?: string | Date
): string | undefined {
  const parsed = parseUtcDate(value)
  if (!parsed) {
    return undefined
  }

  const delta = Date.now() - parsed.valueOf()
  if (delta < 0) {
    return 'now'
  }

  const years = Math.round(delta / year)
  const months = Math.round(delta / month)
  const days = Math.round(delta / day)
  const hours = Math.round(delta / hour)
  const minutes = Math.round(delta / minute)

  if (months >= 12) {
    return `${years}yr`
  }
  if (days >= 31) {
    return `${months}mo`
  }
  if (hours >= 24) {
    return `${days}d`
  }
  if (minutes >= 60) {
    return `${hours}h`
  }
  if (minutes >= 1) {
    return `${minutes}m`
  }
  return 'now'
}

type DateInput = moment.MomentInput | undefined

export type CalendarDateOpts = {
  includeTime?: boolean
  includeYear?: boolean
  includeOrdinal?: boolean
}

export const formatCalendarDate = (
  date: DateInput,
  opts?: CalendarDateOpts
) => {
  if (!date) {
    return undefined
  }

  const isCurrentYear = moment().year() === moment(date).year()
  const displayYear = !isCurrentYear && opts?.includeYear ? ', YYYY' : ''
  const displayOrdinal = opts && opts.includeOrdinal ? 'Do' : 'D'

  const timeFormat = opts?.includeTime ? ' h:mm A' : ''
  return moment(date).calendar(null, {
    nextWeek: '[Next] dddd' + timeFormat, // "Next Wednesday 9:56 PM"
    nextDay: '[Tomorrow]' + timeFormat, // "Tomorrow 9:56 PM"
    sameDay: '[Today]' + timeFormat, // "Today 9:56 PM"
    lastDay: '[Yesterday]' + timeFormat, // "Yesterday 9:56 PM"
    lastWeek: 'dddd' + timeFormat, // "Wednesday 9:56 PM"
    sameElse: `MMM ${displayOrdinal} ${displayYear}` + timeFormat // "Dec 5th, 2022 9:56PM" : "Dec 5, 2022 9:56 PM"
  })
}

export const ageFromDOB = (dob: string) => moment().diff(dob, 'years', false)
